import { useEffect, useRef, useState, useCallback } from "react";
import { useGeo } from "@/context/GeoContext";

const POS_KEY = "piqpath_discover_pos";
const EDGE_SNAP = 40;
const BTN = 44;
const CAPTION = 14; // space below button for "PIQ" caption

/**
 * Phase 7B — small floating, draggable "PIQ" button.
 *
 * - 44×44 globe with a "PIQ" caption (regular weight, ~10px, tracked).
 * - Drag anywhere; snaps to nearest edge when released within 40px.
 * - Position persists in localStorage.
 * - When geo resolves (userGeo goes from null → set), the globe spins
 *   360° once, then briefly flashes the resolved country code before
 *   fading back to the globe.
 */
export default function DiscoverButton({ onOpen }) {
    const { userGeo, geo } = useGeo();
    const country_code = (geo?.country_code || userGeo?.country_code || "").toUpperCase();

    const [pos, setPos] = useState(() => {
        try {
            const raw = localStorage.getItem(POS_KEY);
            if (raw) return JSON.parse(raw);
        } catch { /* ignore */ }
        // Default bottom-right, 24px margin.
        if (typeof window !== "undefined") {
            return {
                x: window.innerWidth - BTN - 24,
                y: window.innerHeight - BTN - CAPTION - 24,
            };
        }
        return { x: 0, y: 0 };
    });
    const [dragging, setDragging] = useState(false);
    const [spinning, setSpinning] = useState(false);
    const [showFlag, setShowFlag] = useState(false);
    const dragRef = useRef({ active: false, dx: 0, dy: 0 });
    const seenGeoRef = useRef(false);

    // Animate when userGeo transitions from null → set.
    useEffect(() => {
        if (!userGeo || seenGeoRef.current) return;
        seenGeoRef.current = true;
        setSpinning(true);
        const t1 = setTimeout(() => {
            setSpinning(false);
            setShowFlag(true);
        }, 800);
        const t2 = setTimeout(() => setShowFlag(false), 1800);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [userGeo]);

    const snapToEdge = useCallback((p) => {
        if (typeof window === "undefined") return p;
        const W = window.innerWidth;
        const H = window.innerHeight;
        const totalH = BTN + CAPTION;
        let { x, y } = p;
        x = Math.max(0, Math.min(W - BTN, x));
        y = Math.max(0, Math.min(H - totalH, y));
        const dL = x;
        const dR = W - BTN - x;
        const dT = y;
        const dB = H - totalH - y;
        const minD = Math.min(dL, dR, dT, dB);
        if (minD < EDGE_SNAP) {
            if (minD === dL) x = 8;
            else if (minD === dR) x = W - BTN - 8;
            else if (minD === dT) y = 8;
            else y = H - totalH - 8;
        }
        return { x, y };
    }, []);

    // Global mouse / touch handlers for the drag lifecycle. Bound to
    // document so the button doesn't lose tracking when the cursor
    // moves faster than the button.
    useEffect(() => {
        if (!dragging) return;
        const onMove = (e) => {
            if (!dragRef.current.active) return;
            const evt = e.touches ? e.touches[0] : e;
            setPos({ x: evt.clientX - dragRef.current.dx, y: evt.clientY - dragRef.current.dy });
        };
        const onUp = () => {
            if (!dragRef.current.active) return;
            dragRef.current.active = false;
            setDragging(false);
            setPos((p) => {
                const snapped = snapToEdge(p);
                try { localStorage.setItem(POS_KEY, JSON.stringify(snapped)); } catch { /* ignore */ }
                return snapped;
            });
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [dragging, snapToEdge]);

    const onDown = useCallback((e) => {
        const evt = e.touches ? e.touches[0] : e;
        dragRef.current = {
            active: true,
            dx: evt.clientX - pos.x,
            dy: evt.clientY - pos.y,
        };
        setDragging(true);
    }, [pos.x, pos.y]);

    // Click vs drag — short-distance up-event counts as click.
    const downSnapshot = useRef({ x: 0, y: 0, t: 0 });
    const onMouseDown = (e) => {
        downSnapshot.current = { x: e.clientX, y: e.clientY, t: Date.now() };
        onDown(e);
    };
    const onMouseUpMaybeClick = (e) => {
        const dx = Math.abs((e.clientX ?? 0) - downSnapshot.current.x);
        const dy = Math.abs((e.clientY ?? 0) - downSnapshot.current.y);
        if (dx + dy < 5 && Date.now() - downSnapshot.current.t < 400) {
            onOpen && onOpen();
        }
    };

    return (
        <div
            data-testid="discover-button"
            style={{
                position: "fixed",
                left: pos.x,
                top: pos.y,
                zIndex: 50,
                touchAction: "none",
                cursor: dragging ? "grabbing" : "grab",
                userSelect: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
            }}
            onMouseDown={onMouseDown}
            onTouchStart={onDown}
            onMouseUp={onMouseUpMaybeClick}
        >
            <div
                aria-label="Open Piqpath explorer"
                role="button"
                style={{
                    width: BTN, height: BTN,
                    borderRadius: BTN / 2,
                    background: "#FFFFFF",
                    border: "1px solid #0A0A0A",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(10,10,10,0.08)",
                    overflow: "hidden",
                    transition: "transform 800ms cubic-bezier(.4,0,.2,1), opacity 220ms ease",
                    transform: spinning ? "rotate(360deg)" : "rotate(0deg)",
                }}
            >
                {showFlag && country_code ? (
                    <span
                        data-testid="discover-country-flash"
                        style={{
                            fontFamily: "Inter, system-ui, sans-serif",
                            fontWeight: 600,
                            fontSize: 14,
                            color: "#0A0A0A",
                            letterSpacing: 0.5,
                        }}
                    >
                        {country_code}
                    </span>
                ) : (
                    <svg
                        width="22" height="22" viewBox="0 0 24 24"
                        fill="none" stroke="#0A0A0A" strokeWidth="1.5"
                        strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden
                    >
                        <circle cx="12" cy="12" r="9" />
                        <ellipse cx="12" cy="12" rx="3.5" ry="9" />
                        <line x1="3" y1="12" x2="21" y2="12" />
                    </svg>
                )}
            </div>
            <span
                data-testid="discover-caption"
                style={{
                    fontFamily: "Inter, system-ui, sans-serif",
                    fontWeight: 400,
                    fontSize: 10,
                    color: "#737373",
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    lineHeight: 1,
                    pointerEvents: "none",
                }}
            >
                PIQ
            </span>
        </div>
    );
}
